import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ConnectorPlatform, SyncCadence } from '@/entities/media-connector';
import { initiateOAuth, MEDIA_CONNECTOR_QUERY_KEYS } from '@/entities/media-connector';
import { ENV } from '@/lib/config/env';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';
import { i18n } from '@/shared/i18n';

interface OAuthCallbackMessage {
  type: 'oauth-callback';
  status: 'success' | 'error';
  platform: ConnectorPlatform | null;
  error: string | null;
}

interface UseOAuthPopupReturn {
  initiateOAuth: (
    platform: ConnectorPlatform,
    title: string,
    tags: string[],
    syncCadence?: SyncCadence,
    /** Carried across the redirect, since there is no moment during it to ask. */
    advertiserId?: string,
    accountId?: string,
  ) => void;
  connectingPlatform: ConnectorPlatform | null;
}

function isOAuthCallbackMessage(data: unknown): data is OAuthCallbackMessage {
  return (
    typeof data === 'object' && data !== null && 'type' in data && data.type === 'oauth-callback'
  );
}

/** Manages the OAuth popup lifecycle for connecting ad-platform accounts. */
export function useOAuthPopup(): UseOAuthPopupReturn {
  const queryClient = useQueryClient();
  const [connectingPlatform, setConnectingPlatform] = useState<ConnectorPlatform | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messageHandledRef = useRef(false);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    popupRef.current = null;
    setConnectingPlatform(null);
  }, []);

  useEffect(() => {
    // The callback page is served by media-api, not the SPA, so the sender's origin is media-api's.
    // Comparing against window.location.origin could never match and silently dropped every result.
    const mediaApiOrigin = new URL(ENV.MEDIA_API_BASE_URL).origin;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== mediaApiOrigin) return;
      if (!isOAuthCallbackMessage(event.data)) return;

      messageHandledRef.current = true;

      if (event.data.status === 'success') {
        if (event.data.platform) {
          track('media_connector_connected', {
            platform: event.data.platform,
            account_id: '',
          });
        }
        notificationService.showSuccess(
          i18n.t('knowledge:mediaConnectors.useOAuthPopup.platformConnectedSuccessfully'),
        );
      } else {
        if (event.data.platform) {
          track('media_connector_connect_failed', {
            platform: event.data.platform,
            error_code: 'oauth_callback_error',
          });
        }
        // Kept on screen until dismissed: it can carry the CM360 accounts to choose from,
        // which appear nowhere else.
        notificationService.showError(
          event.data.error ?? i18n.t('knowledge:mediaConnectors.useOAuthPopup.connectionFailed'),
          { persist: true },
        );
      }

      // Either way: a failed connect can still have saved an account, marked with its error.
      void queryClient.invalidateQueries({ queryKey: MEDIA_CONNECTOR_QUERY_KEYS.all });
      cleanup();
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      cleanup();
    };
  }, [queryClient, cleanup]);

  const startOAuth = useCallback(
    (
      platform: ConnectorPlatform,
      title: string,
      tags: string[],
      syncCadence?: SyncCadence,
      advertiserId?: string,
      accountId?: string,
    ) => {
      setConnectingPlatform(platform);

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        'about:blank',
        'oauth-popup',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,status=yes`,
      );
      if (!popup) {
        notificationService.showError(
          i18n.t('knowledge:mediaConnectors.useOAuthPopup.popupBlocked'),
        );
        cleanup();
        return;
      }
      popupRef.current = popup;

      messageHandledRef.current = false;

      pollRef.current = setInterval(() => {
        if (popupRef.current?.closed) {
          // The message only carries a status, so the connector list has to be refetched either
          // way. Doing it on close as well means a dropped message costs a missing toast rather
          // than a UI that never shows the account the user just connected.
          if (!messageHandledRef.current) {
            void queryClient.invalidateQueries({ queryKey: MEDIA_CONNECTOR_QUERY_KEYS.all });
          }
          cleanup();
        }
      }, 500);

      initiateOAuth(platform, title, tags, syncCadence, advertiserId, accountId)
        .then((authUrl) => {
          if (!popup.closed) {
            popup.location.href = authUrl;
          } else {
            cleanup();
          }
        })
        .catch(() => {
          notificationService.showError(
            i18n.t('knowledge:mediaConnectors.useOAuthPopup.failedToStartConnection'),
          );
          popup.close();
          cleanup();
        });
    },
    [cleanup, queryClient],
  );

  return { initiateOAuth: startOAuth, connectingPlatform };
}
